import React, { memo, useMemo, useCallback, useContext } from 'react'
import TrackedCardCarousel from 'components/Common/TrackedCardCarousel'
import Spacing from 'components/Common/Spacing'
import {
  OFFER_TYPE_TOUR_V2,
} from 'constants/offer'
import YouMayAlsoLikeTile from 'components/OfferList/OfferListTiles/YouMayAlsoLikeTile'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import LayoutContainer from 'components/Common/LayoutContainer'
import { productClick } from 'analytics/enhanced-ecommerce'
import { productClickObject } from 'analytics/eventDefinitions'
import TrackingContext from 'contexts/trackingContext'
import GeoContext from 'contexts/geoContext'
import { buildSearchStringFromFilters } from 'lib/searchUtils'

import Heading from 'components/Luxkit/Typography/Heading'
import uuidV4 from 'lib/uuidV4'
import { useOfferList } from 'hooks/Offers/useOfferList'
import { take, without } from 'lib/arrayUtils'
import useOffers from 'hooks/Offers/useOffers'
import { EmptyObject } from 'lib/objectUtils'

const emptyFilters: App.OfferListFilters = EmptyObject

interface Props {
  title?: string;
  id?: string;
  filters?: App.OfferListFilters;
  trackingDescription: string;
  placeIds: string[];
}

const MINIMUM_OFFERS_TO_DISPLAY = 4
const RECOMMENDATION_TYPES: App.OfferParentType[] = [
  OFFER_TYPE_TOUR_V2,
]

function TourV2YouMayAlsoLikeCarrousel(props: Props) {
  const {
    title = 'You may also like',
    id,
    filters = emptyFilters,
    trackingDescription,
    placeIds,
  } = props

  const geo = useContext(GeoContext)

  const [offerList] = useOfferList({ ...filters, offerTypes: RECOMMENDATION_TYPES, locations: placeIds })
  const offerIds = useMemo(() => take(without(offerList.offerIds, id ?? ''), 8), [id, offerList.offerIds])

  const [offers, offersFetching] = useOffers(offerIds)

  // Used only for tracking
  const algorithm = 'Search Nearby'
  const listName = ['Recommendations', algorithm, trackingDescription].filter(Boolean).join(' - ')
  const tracking = useMemo(() => ({
    category: 'Recommendations',
    label: [algorithm, trackingDescription].filter(Boolean).join(' - '),
    listName,
    listId: uuidV4(),
  }), [trackingDescription, algorithm, listName])

  const onClick = useCallback((
    offer: App.OfferSummary | App.BedbankOfferWithRate,
    position: number,
  ) => {
    productClick(geo.currentCurrency, productClickObject(offer, position), {
      listId: tracking.listId,
      listName,
    })
  }, [listName, tracking.listId, geo])

  if (offers.length < MINIMUM_OFFERS_TO_DISPLAY || offersFetching || offerList.fetching) {
    return null
  }

  return (
    <TrackingContext.Provider value={tracking}>
      <Pane>
        <PaneBody size="xl-y">
          <LayoutContainer>
            <Heading variant="heading4">{title}</Heading>
            <Spacing mt={16}>
              <TrackedCardCarousel pageSize={4}>
                {offers.map((offer, i) => (
                  <YouMayAlsoLikeTile
                    key={offer.id}
                    offer={offer}
                    position={i + 1}
                    onClick={onClick}
                    linkQuery={buildSearchStringFromFilters(filters)}
                  />
                ))}
              </TrackedCardCarousel>
            </Spacing>
          </LayoutContainer>
        </PaneBody>
      </Pane>
    </TrackingContext.Provider>
  )
}

export default memo(TourV2YouMayAlsoLikeCarrousel)
